import { Box, SimpleGrid, Flex, Spinner, useToast } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import ReportFilters from "./components/ReportFilters";
import ReportStatCards from "./components/ReportStatCards";
import PublishingTrendChart from "./components/PublishingTrendChart";
import BroadcastTrendChart from "./components/BroadcastTrendChart";
import ViewsByCategoryChart from "./components/ViewsByCategoryChart";
import TopNewsTable from "./components/TopNewsTable";
import BroadcastStatsPanel from "./components/BroadcastStatsPanel";
import ContentOverviewPanel from "./components/ContentOverviewPanel";
import {
  getOverview,
  getTopNews,
  getViewsByCategory,
  getPublishingTrend,
  getBroadcastStats,
  getBroadcastTrend,
  getContentOverview,
} from "./reportsApi";
import REPORT_TIMEZONE from "constants/reportTimezone";

// YYYY-MM-DD of `date` in the newsroom's timezone. toISOString() gave the UTC
// date, so just after midnight in Beirut "today" was still yesterday.
const toDateInputValue = (date) => date.toLocaleDateString("en-CA", { timeZone: REPORT_TIMEZONE });

// Presets pick the date window: Day = today only, Week = the last 7 days
// (today included), Month = the last 30 days, All = everything ever recorded.
// Custom (isCustomRange=true) skips this and leaves from/to under manual
// control instead.
const ALL_TIME_FROM = "2000-01-01";

function computeRangeForInterval(interval) {
  const to = new Date();
  const from = new Date(to);
  if (interval === "all") return { from: ALL_TIME_FROM, to: toDateInputValue(to) };
  if (interval === "week") from.setDate(to.getDate() - 6);
  else if (interval === "month") from.setDate(to.getDate() - 29);
  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

// The chart bucket size follows the window so a chart never collapses to one
// or two points: a single day is shown per hour, up to ~3 months per day,
// longer ranges per week (or month beyond a year).
function bucketForRange(from, to) {
  const days = (new Date(to) - new Date(from)) / (24 * 60 * 60 * 1000) + 1;
  if (days <= 1) return "hour";
  if (days <= 92) return "day";
  return days <= 400 ? "week" : "month";
}

export default function MainDashboard() {
  const { t } = useTranslation();
  const toast = useToast();

  const [interval, setInterval] = useState("day");
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [from, setFrom] = useState(() => computeRangeForInterval("day").from);
  const [to, setTo] = useState(() => computeRangeForInterval("day").to);

  const selectPreset = (presetInterval) => {
    const nextRange = computeRangeForInterval(presetInterval);
    setInterval(presetInterval);
    setIsCustomRange(false);
    setFrom(nextRange.from);
    setTo(nextRange.to);
  };

  // Custom range doesn't expose a bucket picker — fixed at "day" (the
  // finest granularity) since there's no preset button to imply one here.
  const selectCustom = () => {
    setInterval("day");
    setIsCustomRange(true);
  };

  const resetFilters = () => selectPreset("day");

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topNews, setTopNews] = useState([]);
  const [viewsByCategory, setViewsByCategory] = useState([]);
  const [publishingTrend, setPublishingTrend] = useState([]);
  const [broadcastStats, setBroadcastStats] = useState(null);
  const [broadcastTrend, setBroadcastTrend] = useState([]);
  const [contentOverview, setContentOverview] = useState(null);

  const range = { from, to };
  const bucket = bucketForRange(from, to);

  // Six requests fire per filter change; if the user clicks another filter
  // before they finish, an older, slower batch must not overwrite the newer one.
  const requestIdRef = useRef(0);

  const fetchRangedReports = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const [overviewData, topNewsData, viewsByCategoryData, publishingTrendData, broadcastStatsData, broadcastTrendData] =
        await Promise.all([
          getOverview(range),
          getTopNews({ ...range, limit: 10 }),
          getViewsByCategory(range),
          getPublishingTrend({ ...range, interval: bucket }),
          getBroadcastStats(range),
          getBroadcastTrend({ ...range, interval: bucket }),
        ]);
      if (requestId !== requestIdRef.current) return;
      setOverview(overviewData);
      setTopNews(topNewsData);
      setViewsByCategory(viewsByCategoryData);
      setPublishingTrend(publishingTrendData);
      setBroadcastStats(broadcastStatsData);
      setBroadcastTrend(broadcastTrendData);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch reports."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, bucket, t, toast]);

  useEffect(() => {
    fetchRangedReports();
  }, [fetchRangedReports]);

  // All-time, not affected by the date-range filter — fetched once.
  useEffect(() => {
    getContentOverview()
      .then(setContentOverview)
      .catch((error) => {
        toast({
          title: t("An error occurred."),
          description: error.message || t("Failed to fetch content overview."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card w="100%" p="20px" mb="20px">
        <ReportFilters
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          interval={interval}
          isCustomRange={isCustomRange}
          onSelectPreset={selectPreset}
          onSelectCustom={selectCustom}
          onReset={resetFilters}
        />
      </Card>

      {loading && !overview ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          <ReportStatCards overview={overview} />

          <Box mb="20px">
            <PublishingTrendChart rows={publishingTrend} interval={bucket} />
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px" mb="20px">
            <ViewsByCategoryChart rows={viewsByCategory} />
            <BroadcastTrendChart rows={broadcastTrend} interval={bucket} />
          </SimpleGrid>

          <Box mb="20px">
            <TopNewsTable rows={topNews} />
          </Box>

          <Box mb="20px">
            <BroadcastStatsPanel stats={broadcastStats} />
          </Box>

          <Box mb="20px">
            <ContentOverviewPanel overview={contentOverview} />
          </Box>
        </>
      )}
    </Box>
  );
}
